import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Button } from "@/components/ax/Button";
import { Sheet } from "@/components/ax/Sheet";
import { listDecisions } from "@/lib/decisionStore";
import { useI18n } from "@/i18n";

export const APPLY_COPY = {
  id: {
    title: "Terapkan ke keputusan",
    hint: "Pilih keputusan. Poin masuk sebagai item ASSUMED bersumber pustaka; status hanya berubah oleh Anda di keputusan.",
    pick: "Pilih keputusan",
    create: "Buat keputusan",
    apply: "Terapkan",
    applying: "Menerapkan...",
    close: "Tutup",
    loadFail: "Daftar keputusan belum dapat dimuat. Coba lagi.",
    saveFail: "Poin belum diterapkan. Coba lagi.",
  },
  en: {
    title: "Apply to decision",
    hint: "Pick a decision. The point enters as a library-sourced ASSUMED item; status only changes by you on the decision.",
    pick: "Pick a decision",
    create: "New decision",
    apply: "Apply",
    applying: "Applying...",
    close: "Close",
    loadFail: "The decision list could not be loaded. Try again.",
    saveFail: "The point was not applied. Try again.",
  },
};

/**
 * Sheet pilih-keputusan untuk apply (pola JournalSheet LibraryNotes).
 * onApply(decisionId) mengembalikan {error?}; onApplied() dipanggil saat sukses.
 */
export function ApplyDecisionSheet({ open, onClose, onApply, onApplied, testPrefix = "apply" }) {
  const { lang } = useI18n();
  const copy = APPLY_COPY[lang === "en" ? "en" : "id"];
  const [list, setList] = useState(null);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const triggerRef = useRef(document.activeElement);

  useEffect(() => {
    if (!open) return;
    setPick("");
    setFailed(false);
    listDecisions().then((r) =>
      setList((r.decisions ?? []).filter((d) => d.status !== "committed" && d.status !== "deferred"))
    );
  }, [open ]);

  const save = async () => {
    if (!pick || busy) return;
    setBusy(true);
    setFailed(false);
    const res = await onApply(pick);
    setBusy(false);
    if (res?.error) return setFailed(true);
    onApplied();
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title={copy.title} testId={`${testPrefix}-sheet`} returnFocusRef={triggerRef}>
      <p className="m-0 mb-4 text-sm leading-5 text-ax-text-2" data-testid={`${testPrefix}-hint`}>{copy.hint}</p>
      {!list && <div aria-busy="true" data-testid={`${testPrefix}-skeleton`}><div /><div /><div /></div>}
      {list && list.length === 0 && (
        <Link to="/keputusan/baru" data-testid={`${testPrefix}-create-button`} className="inline-flex min-h-[48px] w-full items-center justify-center rounded-[8px] border border-ax-border px-4 text-sm font-medium text-ax-link hover:underline">
          {copy.create}
        </Link>
      )}
      {list && list.length > 0 && (
        <>
          <fieldset className="m-0 min-w-0 border-0 p-0">
            <legend className="p-0 text-sm font-medium leading-5 text-ax-text">{copy.pick}</legend>
            <div className="mt-2 flex flex-col gap-2">
              {list.map((d) => (
                <label key={d.id} className="relative block">
                  <input type="radio" name={`${testPrefix}-decision`} checked={pick === d.id} onChange={() => setPick(d.id)} data-testid={`${testPrefix}-decision-${d.id}`} className="peer sr-only" />
                  <span className={`flex min-h-[48px] cursor-pointer items-center rounded-[8px] border px-3 py-2 text-base leading-6 [overflow-wrap:anywhere] ${pick === d.id ? "tint-chip-active" : "border-ax-border"}`}>
                    {d.title}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          {failed && <p role="alert" data-testid={`${testPrefix}-error`} className="m-0 mt-3 text-sm leading-5 text-ax-error">{copy.saveFail}</p>}
          <Button variant="primary" disabled={!pick || busy} busy={busy} onClick={save} data-testid={`${testPrefix}-save-button`} className="mt-6 w-full">
            {busy ? copy.applying : copy.apply}
          </Button>
        </>
      )}
      <Button onClick={onClose} data-testid={`${testPrefix}-close-button`} className="mt-2 w-full">{copy.close}</Button>
    </Sheet>
  );
}

export function AppliedMark({ testId }) {
  const { t } = useI18n();
  const done = t("journal.done");
  return (
    <span data-testid={testId} style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "var(--muted)", fontSize: "12px" }}>
      <Check size={14} strokeWidth={1.5} aria-hidden="true" />{done}
    </span>
  );
}
